import React, { useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';

import {
  TreeItem,
  TreeItemLayout,
  Spinner,
  mergeClasses,
} from '@fluentui/react-components';
import {
  ChevronRightRegular,
  ChevronDownRegular,
  ChevronRightFilled,
  ChevronDownFilled,
} from '@fluentui/react-icons';
import { TooltipHost, TooltipOverflowMode } from '@bingads-webui-react/fabric-control';

import { TilesGridCellDropDecorator } from '@bingads-webui-campaign-react/ads-studio-app-shared-components';

import { CustomTab } from './custom-tab';
import { SubTreeItem } from './sub-tree-item';
import { LOADING_CELL, MaxFolderLevel } from '../utils';

export const SubTree = ({
  folderItem,
  loadFolderPathTreeItem,
  loadMoreFolderPathTreeItem,
  i18n,
  level,
  setSize,
  index,
  parentFolderId,
  classes,
  selectedItemId,
  onItemSelected,
  logger,
  onDrop,
}) => {
  const [open, setOpen] = useState({});
  const [loading, setLoading] = useState({});

  const stopPropagation = useCallback(e => e.stopPropagation(), []);

  const handleOpenChange = useCallback((e, openInner, folderId) => {
    stopPropagation(e);
    if (level >= MaxFolderLevel) {
      return;
    }
    if (openInner && folderItem.subFolders.length === 0) {
      setLoading(prevLoadingStatus => ({ ...prevLoadingStatus, [folderId]: true }));
      loadFolderPathTreeItem(folderItem.folderId).finally(() => {
        setLoading(prevLoadingStatus => ({ ...prevLoadingStatus, [folderId]: false }));
      });
    }
    logger.logTrace(`AdStudioNavigation: folder item ${openInner ? 'expanded' : 'collapsed'}`, { navigationItem: folderId });
    setOpen(prevOpenStatus => ({ ...prevOpenStatus, [folderId]: openInner }));
  }, [folderItem.folderId, folderItem.subFolders.length, level, loadFolderPathTreeItem, logger, stopPropagation]);

  const handleTreeItemClicked = useCallback((e, folder) => {
    stopPropagation(e);
    onItemSelected(folder);
    logger.logTrace('AdStudioNavigation: folder item clicked', { navigationItem: folder.folderId });
  }, [logger, onItemSelected, stopPropagation]);

  const expandIcon = useMemo(() => {
    if (loading[folderItem.folderId]) {
      return <Spinner size="tiny" />;
    }
    if (open[folderItem.folderId] && folderItem.subFolders.length > 0) {
      const ChevronDown = selectedItemId === folderItem.folderId ? ChevronDownFilled : ChevronDownRegular;
      return <ChevronDown onClick={e => handleOpenChange(e, false, folderItem.folderId)} />;
    }
    const ChevronRight = selectedItemId === folderItem.folderId ? ChevronRightFilled : ChevronRightRegular;
    return <ChevronRight onClick={e => handleOpenChange(e, true, folderItem.folderId)} />;
  }, [folderItem, handleOpenChange, loading, open, selectedItemId]);

  const checkFolderSelected = useCallback((folderId, subFloders, selectedId) => {
    if (selectedId === folderId) {
      return true;
    }
    if (!open[folderId]) {
      // if current folder is collapsed, need to check if any of its subfolders are selected
      let subFolderSelected = subFloders.some(subFolder => subFolder.folderId === selectedId);
      if (subFolderSelected) {
        return true;
      }
      subFloders.forEach((subFolder) => {
        if (subFolder.subFolders.length > 0) {
          subFolderSelected = checkFolderSelected(subFolder.folderId, subFolder.subFolders, selectedId);
        }
      });
      return subFolderSelected;
    }
    return false;
  }, [open]);

  const isCurrentFolderSelected = useMemo(
    () => checkFolderSelected(folderItem.folderId, folderItem.subFolders, selectedItemId),
    [checkFolderSelected, folderItem.folderId, folderItem.subFolders, selectedItemId]
  );

  const treeItems = useMemo(() => {
    const items = [...folderItem.subFolders];
    if (folderItem.subFolders.length > 0 && folderItem.subFolders.length < folderItem.subFoldersCount) {
      items.push(LOADING_CELL);
    }
    return items;
  }, [folderItem.subFolders, folderItem.subFoldersCount]);

  if (level > MaxFolderLevel) {
    return null;
  }

  return (
    <TreeItem
      value={folderItem.folderId}
      open={open[folderItem.folderId]}
      itemType="branch"
      onOpenChange={e => handleTreeItemClicked(e, folderItem)}
      aria-level={level}
      aria-setsize={setSize}
      aria-posinset={index}
      parentValue={parentFolderId}
    >
      <TilesGridCellDropDecorator
        droppable={!isCurrentFolderSelected}
        onDrop={() => { onDrop(folderItem); }}
        customClassName={classes.customDropDecorator}
      >
        <CustomTab
          value={folderItem.folderId}
          classes={classes}
          isSelected={isCurrentFolderSelected}
        >
          <TreeItemLayout
            expandIcon={expandIcon}
            className={mergeClasses(classes.treeItemLayout, isCurrentFolderSelected && classes.treeSelected)}
          >
            <TooltipHost
              overflowMode={TooltipOverflowMode.Self}
              content={folderItem.folderName}
              hostClassName={classes.tooltipHost}
            >
              {folderItem.folderName}
            </TooltipHost>
          </TreeItemLayout>
        </CustomTab>
      </TilesGridCellDropDecorator>
      {
        open[folderItem.folderId] && treeItems.length > 0 && treeItems.map((subFolder, i) =>
          (
            <SubTreeItem
              folderItem={subFolder}
              level={level + 1}
              setSize={folderItem.subFolders.length}
              posinset={i + 1}
              classes={classes}
              selectedItemId={selectedItemId}
              onRenderSubTree={folder => (
                <SubTree
                  folderItem={folder}
                  loadFolderPathTreeItem={loadFolderPathTreeItem}
                  loadMoreFolderPathTreeItem={loadMoreFolderPathTreeItem}
                  i18n={i18n}
                  level={level + 1}
                  setSize={folder.subFolders.length}
                  index={i + 1}
                  parentFolderId={folder.folderId}
                  classes={classes}
                  onItemSelected={onItemSelected}
                  selectedItemId={selectedItemId}
                  logger={logger}
                  onDrop={onDrop}
                />
                )}
              onLeafTreeItemClicked={(e) => {
                stopPropagation(e);
                onItemSelected(subFolder);
              }}
              loadMoreFolderPathTreeItem={loadMoreFolderPathTreeItem}
              key={subFolder.folderId}
              parentFolder={folderItem}
              onDrop={() => { onDrop(subFolder); }}
            />
          ))
      }
    </TreeItem>
  );
};

SubTree.propTypes = {
  folderItem: PropTypes.shape({
    folderId: PropTypes.number,
    folderName: PropTypes.string,
    subFolders: PropTypes.arrayOf(PropTypes.shape({})),
    subFoldersCount: PropTypes.number,
  }).isRequired,
  loadFolderPathTreeItem: PropTypes.func.isRequired,
  loadMoreFolderPathTreeItem: PropTypes.func.isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  level: PropTypes.number.isRequired,
  setSize: PropTypes.number.isRequired,
  index: PropTypes.number.isRequired,
  parentFolderId: PropTypes.number,
  classes: PropTypes.shape({
    treeItemLayout: PropTypes.string,
    treeSelected: PropTypes.string,
    customDropDecorator: PropTypes.string,
  }).isRequired,
  onItemSelected: PropTypes.func.isRequired,
  selectedItemId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  logger: PropTypes.shape({
    logTrace: PropTypes.func,
  }).isRequired,
  onDrop: PropTypes.func.isRequired,
};

SubTree.defaultProps = {
  parentFolderId: null,
  selectedItemId: null,
};
